import {Component, createRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMUtils from '@livongo/utils/dom';
import isEmpty from 'lodash/isEmpty';
import {withTranslation} from 'react-i18next';
import Form from 'ui/Form';
import Radio from 'ui/Radio';
import RadioGroup from 'ui/RadioGroup';
import TextArea from 'ui/TextArea';
import {FieldTypes} from 'utils/page';
import WithPage from '../WithPage';

const {OTHER_PLACEHOLDER, QUESTION} = FieldTypes;

class Question extends Component {
    static propTypes = {
        data: PropTypes.object.isRequired,
        previousAnswers: PropTypes.object,
        isNextDisabled: PropTypes.bool,
        onSetNextDisabled: PropTypes.func.isRequired,
        onQuestionPageShown: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    };
    _form = createRef();

    constructor(props) {
        super(props);

        const {t, data, previousAnswers} = props;

        const hasPreviousAnswerText = !isEmpty(
            previousAnswers.selectedOtherAnswerText
        );

        this._previousAnswerText = hasPreviousAnswerText
            ? previousAnswers.selectedOtherAnswerText.value
            : null;
        this._textFields = data.getTextFieldsMap();

        const placeholder = this._textFields.get(OTHER_PLACEHOLDER);

        this._question = this._textFields.get(QUESTION).getTextValue();
        this._answers = data.getAnswersList();
        this._inputPlaceholder = placeholder
            ? placeholder.getTextValue()
            : t('writeResponseHere');

        this.state = {
            showTextArea: hasPreviousAnswerText,
            isInputValid: true,
        };
    }

    componentDidMount() {
        const {
            onSetNextDisabled,
            previousAnswers,
            onQuestionPageShown,
        } = this.props;

        onSetNextDisabled(isEmpty(previousAnswers));
        onQuestionPageShown(this._form.current);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const textInput = document.querySelector('.TextArea');

        if (textInput) {
            textInput.scrollIntoView();
        }
    }

    _onRadioChange = evt => {
        const {isNextDisabled, onSetNextDisabled} = this.props;

        if (isNextDisabled) {
            onSetNextDisabled(false);
        }

        this.setState({
            showTextArea: Boolean(DOMUtils.attr(evt.target, 'data-has-input')),
            isInputValid: true,
        });
    };

    _onInputInvalid = validationState => {
        const isInputValid = validationState ? validationState.isValid : true;

        this.setState({
            isInputValid,
        });
    };

    _onInputChange = evt => {
        const {isInputValid} = this.state;

        if (!isInputValid) {
            this.setState({
                isInputValid: true,
            });
        }
    };

    _isChecked = answer => {
        const {previousAnswers} = this.props;

        if (isEmpty(previousAnswers)) {
            return false;
        }

        const hasCustomAnswer = !isEmpty(
            previousAnswers.selectedOtherAnswerText
        );
        const previousAnswersIds = previousAnswers.selectedAnswerCmsIdsList;
        const hasSameId = previousAnswersIds.includes(answer.getAnswerCmsId());

        return hasCustomAnswer || hasSameId;
    };

    submit() {
        return this._form.current.submit();
    }

    render() {
        const {previousAnswers} = this.props;
        const {showTextArea, isInputValid} = this.state;
        const radioGroupClasses = classNames({
            'FormElement-contains-error': !isInputValid,
        });

        return (
            <div className="Question">
                <div className="Question-content">
                    <Form ref={this._form}>
                        <RadioGroup
                            className={radioGroupClasses}
                            name="answer_cms_id"
                            label={this._question}
                            onChange={this._onRadioChange}
                        >
                            {this._answers.map(answer => {
                                const id = answer.getAnswerCmsId();
                                const checked = this._isChecked(answer);

                                return (
                                    <Radio
                                        key={id}
                                        value={id}
                                        label={answer.getAnswerText()}
                                        hasInput={answer.hasInput}
                                        defaultChecked={checked}
                                    />
                                );
                            })}
                        </RadioGroup>
                        {showTextArea && (
                            <TextArea
                                name="answer_text"
                                required
                                autofocus={
                                    previousAnswers &&
                                    isEmpty(
                                        previousAnswers.selectedOtherAnswerText
                                    )
                                }
                                ariaLabel={this._inputPlaceholder}
                                placeholder={this._inputPlaceholder}
                                defaultValue={this._previousAnswerText}
                                onInvalid={this._onInputInvalid}
                                onChange={this._onInputChange}
                            />
                        )}
                    </Form>
                </div>
            </div>
        );
    }
}

export default withTranslation()(WithPage(Question));
